import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const HTMLSemanticIntro = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Semantic HTML5</div>
                <h1>Why Semantic HTML Matters</h1>
                <p className="hero-lead">
                    Discover why semantic HTML is crucial for accessibility, SEO, and maintainability.
                    Learn the difference between semantic and non-semantic elements and when to use each.
                </p>
            </div>

            <div className="content-section">
                <h2>What is Semantic HTML?</h2>
                <p>
                    <strong>Semantic HTML</strong> uses elements that clearly describe their meaning and purpose
                    to both the browser and developer. The element name tells you what the content is about.
                </p>

                <div className="comparison-example">
                    <div className="code-example error">
                        <div className="code-header">
                            <span> Non-Semantic (Meaningless)</span>
                        </div>
                        <CodeBlock language="html" code={`<div class="header">
    <div class="nav">
        <div class="nav-item">Home</div>
        <div class="nav-item">About</div>
    </div>
</div>

<div class="article">
    <div class="title">Article Title</div>
    <div class="content">Article content...</div>
</div>

<div class="footer">
    <div>Copyright 2024</div>
</div>`} />
                    </div>

                    <div className="code-example">
                        <div className="code-header">
                            <span>Semantic (Meaningful)</span>
                        </div>
                        <CodeBlock language="html" code={`<header>
    <nav>
        <a href="/">Home</a>
        <a href="/about">About</a>
    </nav>
</header>

<article>
    <h1>Article Title</h1>
    <p>Article content...</p>
</article>

<footer>
    <p>Copyright 2024</p>
</footer>`} />
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Why Semantic HTML Matters</h2>

                <div className="benefits-grid">
                    <div className="benefit-card">
                        <h3> Accessibility</h3>
                        <ul>
                            <li>Screen readers understand page structure</li>
                            <li>Users can navigate by landmarks</li>
                            <li>Assistive technologies work better</li>
                            <li>Better keyboard navigation</li>
                        </ul>
                    </div>

                    <div className="benefit-card">
                        <h3> SEO Benefits</h3>
                        <ul>
                            <li>Search engines understand content hierarchy</li>
                            <li>Better content indexing</li>
                            <li>Improved search rankings</li>
                            <li>Rich snippets in search results</li>
                        </ul>
                    </div>

                    <div className="benefit-card">
                        <h3> Developer Experience</h3>
                        <ul>
                            <li>Code is self-documenting</li>
                            <li>Easier to read and maintain</li>
                            <li>Faster onboarding for new developers</li>
                            <li>Less need for comments</li>
                        </ul>
                    </div>

                    <div className="benefit-card">
                        <h3> Consistency</h3>
                        <ul>
                            <li>Standard meaning across sites</li>
                            <li>Browser default styling makes sense</li>
                            <li>Future-proof code</li>
                            <li>Works with web standards</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Semantic vs Non-Semantic Elements</h2>

                <div className="elements-comparison">
                    <h3>Non-Semantic Elements</h3>
                    <p>Tell nothing about content:</p>
                    <ul>
                        <li><code>&lt;div&gt;</code> - Generic container</li>
                        <li><code>&lt;span&gt;</code> - Generic inline container</li>
                    </ul>

                    <h3>Semantic Elements</h3>
                    <p>Clearly describe content:</p>
                    <div className="semantic-list">
                        <div className="semantic-category">
                            <h4>Document Structure:</h4>
                            <ul>
                                <li><code>&lt;header&gt;</code> - Page or section header</li>
                                <li><code>&lt;nav&gt;</code> - Navigation links</li>
                                <li><code>&lt;main&gt;</code> - Main content</li>
                                <li><code>&lt;footer&gt;</code> - Page or section footer</li>
                                <li><code>&lt;aside&gt;</code> - Side content</li>
                            </ul>
                        </div>

                        <div className="semantic-category">
                            <h4>Content Grouping:</h4>
                            <ul>
                                <li><code>&lt;article&gt;</code> - Independent content</li>
                                <li><code>&lt;section&gt;</code> - Thematic grouping</li>
                                <li><code>&lt;figure&gt;</code> - Self-contained content</li>
                                <li><code>&lt;figcaption&gt;</code> - Figure caption</li>
                            </ul>
                        </div>

                        <div className="semantic-category">
                            <h4>Text Meaning:</h4>
                            <ul>
                                <li><code>&lt;strong&gt;</code> - Important text</li>
                                <li><code>&lt;em&gt;</code> - Emphasized text</li>
                                <li><code>&lt;mark&gt;</code> - Highlighted text</li>
                                <li><code>&lt;time&gt;</code> - Date/time</li>
                                <li><code>&lt;code&gt;</code> - Code snippet</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Real-World Impact</h2>

                <h3>Screen Reader Navigation</h3>
                <div className="impact-example">
                    <p>With semantic HTML, screen reader users can:</p>
                    <ul>
                        <li>Jump directly to <code>&lt;nav&gt;</code> to find navigation</li>
                        <li>Skip to <code>&lt;main&gt;</code> for main content</li>
                        <li>List all <code>&lt;article&gt;</code> elements</li>
                        <li>Navigate by headings hierarchy</li>
                    </ul>
                    <p><strong>With only divs:</strong> Must listen to entire page linearly</p>
                </div>

                <h3>SEO Example</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Search engines understand this structure: --&gt;
<article>
    <header>
        <h1>Complete Guide to HTML Semantics</h1>
        <time datetime="2024-12-31">December 31, 2024</time>
        <address>By <a href="/author">John Doe</a></address>
    </header>
    
    <section>
        <h2>Introduction</h2>
        <p>Content...</p>
    </section>
    
    <section>
        <h2>Main Points</h2>
        <p>Content...</p>
    </section>
    
    <footer>
        <p>Tags: <a href="/tag/html">HTML</a></p>
    </footer>
</article>

<!-- Google can identify: --&gt;
<!-- - Article title and content --&gt;
<!-- - Publication date --&gt;
<!-- - Author information --&gt;
<!-- - Article sections --&gt;
<!-- - Related tags --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>When to Use Semantic vs Non-Semantic</h2>

                <div className="usage-guide">
                    <div className="usage-card">
                        <h3>Use Semantic Elements When:</h3>
                        <ul>
                            <li>Content has clear meaning (article, navigation, etc.)</li>
                            <li>Structure represents page hierarchy</li>
                            <li>Want better accessibility</li>
                            <li>Need SEO benefits</li>
                            <li>Content is self-contained</li>
                        </ul>
                    </div>

                    <div className="usage-card">
                        <h3>Use &lt;div&gt; When:</h3>
                        <ul>
                            <li>Styling/layout purposes only</li>
                            <li>Wrapping elements for CSS</li>
                            <li>No semantic meaning exists</li>
                            <li>Grid/flexbox containers</li>
                            <li>JavaScript target containers</li>
                        </ul>
                    </div>
                </div>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Good mix of semantic and non-semantic: --&gt;
<article>  <!-- Semantic: independent content --&gt;
    <header>  <!-- Semantic: article header --&gt;
        <h1>Title</h1>
    </header>
    
    <div class="grid">  <!-- Non-semantic: layout only --&gt;
        <div class="col">  <!-- Non-semantic: layout only --&gt;
            <section>  <!-- Semantic: thematic content --&gt;
                <h2>Section Title</h2>
                <p>Content...</p>
            </section>
        </div>
    </div>
</article>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Common Mistakes</h2>

                <div className="mistakes-list">
                    <h3> Mistake 1: Using div for Everything</h3>
                    <div className="code-example error">
                        <CodeBlock language="html" code={`<div class="navigation">
    <div class="link">Home</div>
    <div class="link">About</div>
</div>`} />
                    </div>
                    <div className="code-example">
                        <div className="code-header">
                            <span>Better:</span>
                        </div>
                        <CodeBlock language="html" code={`<nav>
    <a href="/">Home</a>
    <a href="/about">About</a>
</nav>`} />
                    </div>

                    <h3> Mistake 2: Multiple &lt;main&gt; Elements</h3>
                    <div className="code-example error">
                        <CodeBlock language="html" code={`<body>
    <main>Content 1</main>  <!--  --&gt;
    <main>Content 2</main>  <!--  Only one main per page! --&gt;
</body>`} />
                    </div>

                    <h3> Mistake 3: Misusing &lt;article&gt; and &lt;section&gt;</h3>
                    <div className="code-example error">
                        <CodeBlock language="html" code={`<!-- Don't use article for layout --&gt;
<article class="sidebar">  <!--  --&gt;
    <div>Ad</div>
</article>`} />
                    </div>
                    <div className="code-example">
                        <div className="code-header">
                            <span>Better:</span>
                        </div>
                        <CodeBlock language="html" code={`<aside>  <!-- Semantic: supplementary content --&gt;
    <div>Ad</div>
</aside>`} />
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Testing Semantic HTML</h2>

                <h3>Tools:</h3>
                <ul>
                    <li><strong>WAVE:</strong> Browser extension for accessibility testing</li>
                    <li><strong>HeadingsMap:</strong> Visualize heading structure</li>
                    <li><strong>Lighthouse:</strong> Built into Chrome DevTools</li>
                    <li><strong>Screen Readers:</strong> NVDA (Windows), VoiceOver (Mac)</li>
                    <li><strong>W3C Validator:</strong> Validate HTML structure</li>
                </ul>

                <h3>Manual Checks:</h3>
                <ul>
                    <li>Can you understand page structure without CSS?</li>
                    <li>Do element names describe their content?</li>
                    <li>Is heading hierarchy logical (h1  h2  h3)?</li>
                    <li>Can you navigate with keyboard only?</li>
                    <li>Does page make sense to screen reader?</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Best Practices</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use semantic elements when meaning exists</li>
                            <li>Maintain logical heading hierarchy</li>
                            <li>One &lt;main&gt; per page</li>
                            <li>Use &lt;nav&gt; for navigation</li>
                            <li>Use &lt;article&gt; for independent content</li>
                            <li>Test with screen readers</li>
                            <li>Think about accessibility first</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Use &lt;div&gt; when semantic element exists</li>
                            <li>Skip heading levels</li>
                            <li>Multiple &lt;main&gt; elements</li>
                            <li>Use semantic elements for styling only</li>
                            <li>Ignore accessibility</li>
                            <li>Forget keyboard users</li>
                            <li>Rely on visual appearance alone</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Quick Reference</h2>
                <div className="quick-reference">
                    <table>
                        <thead>
                            <tr>
                                <th>Element</th>
                                <th>Purpose</th>
                                <th>When to Use</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>&lt;header&gt;</code></td>
                                <td>Introductory content</td>
                                <td>Page/section header with title, logo, nav</td>
                            </tr>
                            <tr>
                                <td><code>&lt;nav&gt;</code></td>
                                <td>Navigation links</td>
                                <td>Main navigation, table of contents</td>
                            </tr>
                            <tr>
                                <td><code>&lt;main&gt;</code></td>
                                <td>Main content</td>
                                <td>Primary content (one per page)</td>
                            </tr>
                            <tr>
                                <td><code>&lt;article&gt;</code></td>
                                <td>Independent content</td>
                                <td>Blog posts, news articles, comments</td>
                            </tr>
                            <tr>
                                <td><code>&lt;section&gt;</code></td>
                                <td>Thematic grouping</td>
                                <td>Chapters, tabs, themed content blocks</td>
                            </tr>
                            <tr>
                                <td><code>&lt;aside&gt;</code></td>
                                <td>Related content</td>
                                <td>Sidebars, pull quotes, ads</td>
                            </tr>
                            <tr>
                                <td><code>&lt;footer&gt;</code></td>
                                <td>Closing content</td>
                                <td>Copyright, author, related links</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

        </div>
    );
};

export default HTMLSemanticIntro;




